import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Download, FileText, Play, Wrench, Lock } from "lucide-react";
import Layout, { Overline } from "../components/Layout";
import PageHero from "../components/PageHero";
import { Reveal } from "../components/Motion";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { IMAGES, RESOURCE_CATEGORIES, RESOURCES } from "../data/site";

const typeIcon = (t) => {
  if (t === "Video") return <Play size={16} />;
  if (t === "ZIP") return <Wrench size={16} />;
  if (t === "Article") return <FileText size={16} />;
  return <Download size={16} />;
};

export default function Resources() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [gated, setGated] = useState(null);

  const filtered = useMemo(() => {
    return RESOURCES.filter((r) => {
      const matchCat = cat === "All" || r.category === cat;
      const matchQ = !q || (r.title + r.desc + r.category).toLowerCase().includes(q.toLowerCase());
      return matchCat && matchQ;
    });
  }, [q, cat]);

  return (
    <Layout
      title="Resources"
      description="A knowledge hub for Indigenous land management — field manuals, species guides, videos, templates, articles, case studies, grant guides and toolkits from Ranger Base."
    >
      <PageHero
        overline="Knowledge Hub"
        titleLines={["Knowledge to", "share on Country"]}
        subtitle="Field manuals, species guides, templates and toolkits — practical resources for ranger teams. New material is added regularly."
        image={IMAGES.eucalyptusLeaf}
      />

      <section className="py-16 lg:py-24 bg-warmwhite">
        <div className="mx-auto max-w-[88rem] px-6 lg:px-10">
          {/* Search */}
          <div className="relative max-w-2xl">
            <Search size={20} className="absolute left-5 top-1/2 -translate-y-1/2 text-charcoal/40" />
            <input
              data-testid="resource-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search resources…"
              className="w-full rounded-full border border-charcoal/15 bg-white pl-14 pr-6 py-4 text-lg focus:outline-none focus:border-eucalyptus transition-colors"
            />
          </div>

          {/* Categories */}
          <div className="flex flex-wrap gap-2 mt-8">
            {RESOURCE_CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`resource-cat-${c.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => setCat(c)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${cat === c ? "bg-eucalyptus text-warmwhite" : "bg-charcoal/5 text-charcoal hover:bg-charcoal/10"}`}
              >
                {c}
              </button>
            ))}
          </div>

          {/* Results */}
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((r, i) => (
              <Reveal key={r.title} delay={(i % 3) * 0.05}>
                <div data-testid={`resource-card-${i}`} className="group h-full rounded-3xl border border-charcoal/10 bg-white p-6 hover:-translate-y-1 hover:border-eucalyptus/40 transition-[transform,border-color] duration-300 flex flex-col">
                  <div className="flex items-center justify-between">
                    <span className="text-xs tracking-overline uppercase text-earth">{r.category}</span>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="inline-flex items-center gap-1 text-[10px] font-medium text-ochre"><Lock size={11} /> Members</span>
                      <span className="text-[11px] font-mono px-2 py-1 rounded-md bg-sand text-charcoal/70">{r.type}</span>
                    </span>
                  </div>
                  <h3 className="mt-4 font-heading text-xl font-semibold tracking-tight text-charcoal">{r.title}</h3>
                  <p className="mt-2 text-sm text-charcoal/65 leading-relaxed flex-1">{r.desc}</p>
                  <button onClick={() => setGated(r)} data-testid={`resource-download-${i}`} className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-eucalyptus group-hover:gap-3 transition-all">
                    <Lock size={14} /> {r.type === "Video" ? "Watch" : r.type === "Article" ? "Read" : "Download"}
                  </button>
                </div>
              </Reveal>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="mt-16 text-center text-charcoal/50" data-testid="resource-empty">
              <p className="font-heading text-2xl">No resources found.</p>
              <p className="mt-2">Try a different search or category.</p>
            </div>
          )}

          <p className="mt-12 text-sm text-charcoal/50">All resources are available to Ranger Base members. Become a member to unlock the full knowledge hub — new material is added regularly.</p>
        </div>
      </section>

      <Dialog open={!!gated} onOpenChange={(o) => !o && setGated(null)}>
        <DialogContent className="max-w-md rounded-3xl border-charcoal/10 bg-warmwhite p-0 overflow-hidden" data-testid="members-gate">
          <div className="bg-eucalyptus text-warmwhite p-8 relative overflow-hidden">
            <div className="grain absolute inset-0 opacity-30" />
            <span className="relative inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-warmwhite/15"><Lock size={24} /></span>
            <p className="relative mt-5 text-[11px] tracking-overline uppercase text-sandstone/80">Members only</p>
            <DialogTitle className="relative mt-2 font-heading text-2xl font-semibold tracking-tight">You must be a member to access this resource.</DialogTitle>
          </div>
          <div className="p-8">
            {gated && <p className="text-sm text-charcoal/60 mb-1">Requested resource</p>}
            {gated && <p className="font-heading text-lg font-semibold text-charcoal">{gated.title}</p>}
            <DialogDescription className="mt-4 text-charcoal/70 leading-relaxed">
              Ranger Base resources are shared with our members and partner organisations. Become a
              member to unlock field manuals, species guides, templates, videos and toolkits.
            </DialogDescription>
            <div className="mt-6 flex flex-col gap-3">
              <Link
                to="/contact?enquiry=General&subject=Membership%20access"
                data-testid="gate-become-member"
                onClick={() => setGated(null)}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-eucalyptus text-warmwhite px-6 py-3 font-semibold hover:bg-eucalyptus-deep transition-colors"
              >
                <Lock size={16} /> Become a member
              </Link>
              <button
                onClick={() => setGated(null)}
                data-testid="gate-close"
                className="inline-flex items-center justify-center rounded-full border border-charcoal/15 px-6 py-3 font-semibold text-charcoal hover:bg-charcoal/5 transition-colors"
              >
                Maybe later
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
